export interface UserProfile {
  id: string;
  email: string;
  fullName: string;
  role: 'customer_admin' | 'customer_user';
  customerId: string;
  avatarUrl?: string;
  status: 'active' | 'suspended';
}

export interface QuotaAccount {
  customerId: string;
  monthlyLimit: number;
  bonus: number;
  consumed: number;
  reserved: number;
  available: number;
  periodEnd: string;
}

export interface QuotaTransaction {
  id: string;
  customerId: string;
  type: 'reservation' | 'consume' | 'release' | 'bonus' | 'adjustment' | 'reversal';
  amount: number;
  balanceAfter: number;
  referenceJobId?: string;
  referenceLessonId?: string;
  reason?: string;
  createdAt: string;
}

export interface Project {
  id: string;
  customerId: string;
  name: string;
  description?: string;
  subject?: string;
  coverImage?: string;
  lessonCount: number;
  status: 'active' | 'archived';
  createdAt: string;
  updatedAt: string;
}

export interface LessonSceneDefaults {
  voiceId: string | null;
  speechRate: number;
  startDelaySeconds: number;
  transitionDelaySeconds: number;
  endDelaySeconds: number;
  transitionText: string | null;
  transitionPosition: 'before' | 'after' | 'none';
  visualTransition: 'cut' | 'fade';
  visualTransitionDurationSeconds: number;
  outputWidth: number;
  outputHeight: number;
  outputFps: number;
  normalizeAudio: boolean;
  outputVolume: number;
}

export const DEFAULT_LESSON_SCENE_DEFAULTS: LessonSceneDefaults = {
  voiceId: 'faber',
  speechRate: 1,
  startDelaySeconds: 1.5,
  transitionDelaySeconds: 0.5,
  endDelaySeconds: 1,
  transitionText: null,
  transitionPosition: 'none',
  visualTransition: 'cut',
  visualTransitionDurationSeconds: 0,
  outputWidth: 1280,
  outputHeight: 720,
  outputFps: 25,
  normalizeAudio: true,
  outputVolume: 1,
};

export function resolveSceneSettings(
  lessonDefaults?: LessonSceneDefaults,
  sceneOverrides?: Partial<LessonSceneDefaults>
): LessonSceneDefaults {
  return {
    ...DEFAULT_LESSON_SCENE_DEFAULTS,
    ...(lessonDefaults || {}),
    ...(sceneOverrides || {}),
  };
}

export interface Lesson {
  id: string;
  customerId: string;
  projectId: string;
  title: string;
  description?: string;
  order: number;
  voiceId: string;
  renderProfile: string;
  sceneCount: number;
  sceneDefaults: LessonSceneDefaults;
  totalEstimatedDuration?: number;
  totalRenderedDuration?: number;
  contentRevision?: number;
  lastRenderedRevision?: number;
  status: 'draft' | 'ready' | 'rendering' | 'completed' | 'error';
  durationSeconds?: number;
  outputId?: string;
  videoUrl?: string;
  lastRenderedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Scene {
  id: string;
  customerId: string;
  lessonId: string;
  sceneNumber: number;
  slideNumber: number;
  order: number;
  imagePath?: string;
  imageUrl?: string;
  narration: string;
  text?: string;
  overrides?: Partial<LessonSceneDefaults>;
  narrationAudioPath?: string;
  narrationAudioUrl?: string;
  transitionAudioPath?: string;
  transitionAudioUrl?: string;
  audioPath?: string;
  audioUrl?: string;
  audioDurationSeconds?: number;
  duration?: number;
  wordCount: number;
  revision?: number;
  validationStatus?: 'valid' | 'warning' | 'invalid';
  renderStatus?: 'draft' | 'audio_ready' | 'rendered' | 'error';
  status: 'draft' | 'audio_ready' | 'rendering' | 'error';
  createdAt: string;
  updatedAt: string;
}

export interface RenderJob {
  id: string;
  customerId: string;
  projectId: string;
  lessonId: string;
  status:
    | 'queued'
    | 'validating'
    | 'downloading'
    | 'generating_audio'
    | 'rendering_scenes'
    | 'assembling_video'
    | 'uploading'
    | 'validating_output'
    | 'completed'
    | 'failed'
    | 'cancelled';
  progress: number;
  currentStep: string;
  currentScene: number;
  totalScenes: number;
  completedScenes: number;
  errorMessage?: string;
  logs?: string[];
  createdAt: string;
  startedAt?: string;
  finishedAt?: string;
}

export interface RenderOutput {
  id: string;
  customerId: string;
  projectId: string;
  projectName?: string;
  lessonId: string;
  lessonTitle?: string;
  jobId: string;
  videoUrl: string;
  thumbnailUrl?: string;
  durationSeconds: number;
  resolution: string;
  fileSize: number;
  format: 'mp4';
  createdAt: string;
}

export interface ClientDashboardData {
  customer: {
    id: string;
    responsibleName: string;
    planName: string;
    nextRenewal: string;
  };
  quota: QuotaAccount;
  stats: {
    totalProjects: number;
    totalLessons: number;
    totalVideos: number;
    activeJobsCount: number;
  };
  recentVideos: RenderOutput[];
}

export interface BatchImportScenePreview {
  sceneNumber: number;
  slideNumber: number;
  narration: string;
  hasImage: boolean;
  hasScript: boolean;
  imageFileName?: string;
  previewImageUrl?: string;
  validationStatus: 'valid' | 'invalid' | 'pending';
}

export interface BatchImportLessonPreview {
  tempId: string;
  folderName: string;
  title: string;
  order: number;
  slideCount: number;
  scriptCount: number;
  validSceneCount: number;
  inconsistentSceneCount: number;
  issues: string[];
  scenes: BatchImportScenePreview[];
}

export interface BatchImportAnalyzeResult {
  batchId: string;
  projectId: string;
  projectName: string;
  zipFileName: string;
  lessons: BatchImportLessonPreview[];
  totalLessons: number;
  totalSlides: number;
  totalScenes: number;
  hasInconsistencies: boolean;
}

export interface BatchImportConfirmPayload {
  batchId: string;
  lessons: {
    tempId: string;
    title: string;
    order: number;
  }[];
}

// Google Drive Integration V2.0 Types
export type GoogleDriveConnectionStatus =
  | 'not_configured'
  | 'connecting'
  | 'connected'
  | 'validating'
  | 'valid'
  | 'expired'
  | 'authorization_error'
  | 'disconnected';

export interface GoogleDriveConnectionSettings {
  maxConcurrentUploads: number;
  maxAttempts: number;
  duplicatePolicy: 'skip_identical' | 'create_version';
  rootFolderName: string;
}

export interface GoogleDriveConnection {
  id?: string;
  customerId?: string;
  connectedBy?: string;
  googleAccountId?: string;
  googleAccountEmail?: string;
  googleAccountName?: string;
  googleAccountAvatar?: string;
  rootFolderId?: string;
  rootFolderName?: string;
  connectionStatus: GoogleDriveConnectionStatus;
  isConfigured?: boolean;
  serviceName?: string;
  grantedScopes?: string[];
  settings: GoogleDriveConnectionSettings;
  connectedAt?: string;
  lastTestAt?: string;
  lastTestStatus?: 'success' | 'failed';
  lastTestError?: string;
  updatedAt?: string;
}

// YouTube Integration V1.0 Types
export type YouTubeConnectionStatus =
  | 'not_configured'
  | 'connecting'
  | 'connected'
  | 'validating'
  | 'valid'
  | 'insufficient_permissions'
  | 'expired'
  | 'error'
  | 'disconnected';

export interface YouTubeTransferSettings {
  id?: string;
  customerId?: string;
  userId?: string;
  connectionId?: string;
  defaultLanguage: string;
  defaultCategoryId: string;
  titleTemplate: string;
  descriptionTemplate: string;
  playlistPrivacy: 'unlisted';
  videoPrivacy: 'private';
  defaultAudienceClassification: boolean;
  uploadConcurrency: number;
  duplicatePolicy: 'skip_identical';
  updatedAt?: string;
}

export interface YouTubeConnection {
  id?: string;
  customerId?: string;
  userId?: string;
  googleAccountId?: string;
  channelId: string;
  channelTitle: string;
  channelThumbnail?: string;
  channelCustomUrl?: string;
  grantedScopes?: string[];
  status: YouTubeConnectionStatus;
  connectionStatus: YouTubeConnectionStatus;
  isConfigured?: boolean;
  serviceName?: string;
  settings?: YouTubeTransferSettings;
  connectedAt?: string;
  lastTestAt?: string;
  lastTestStatus?: 'success' | 'failed';
  lastTestResult?: string;
  updatedAt?: string;
}

export interface YouTubeProjectPlaylist {
  id: string;
  customerId: string;
  userId: string;
  connectionId: string;
  channelId: string;
  projectId: string;
  playlistId: string;
  playlistTitle: string;
  playlistUrl: string;
  privacyStatus: 'unlisted';
  createdAt: string;
  lastValidatedAt?: string;
}

export type TransferProvider = 'google_drive' | 'youtube';

export type TransferBatchStatus =
  | 'queued'
  | 'in_progress'
  | 'completed'
  | 'completed_with_errors'
  | 'failed'
  | 'cancelled';

export interface TransferBatch {
  id: string;
  customerId: string;
  createdBy: string;
  userId?: string;
  provider?: TransferProvider;
  connectionId: string;
  googleAccountEmail?: string;
  channelId?: string;
  channelTitle?: string;
  rootFolderName?: string;
  projectIds: string[];
  projectNames?: string[];
  totalFiles: number;
  completedFiles: number;
  failedFiles: number;
  skippedFiles: number;
  totalBytes: number;
  transferredBytes: number;
  status: TransferBatchStatus;
  createdAt: string;
  startedAt?: string;
  finishedAt?: string;
  errorMessage?: string;
}

export type TransferJobStatus =
  | 'queued'
  | 'preparing'
  | 'creating_playlist'
  | 'checking_destination'
  | 'uploading'
  | 'uploaded'
  | 'processing_youtube'
  | 'adding_to_playlist'
  | 'verifying'
  | 'completed'
  | 'skipped'
  | 'failed'
  | 'cancelled'
  | 'awaiting_authorization'
  | 'quota_exceeded';

export interface TransferJob {
  id: string;
  batchId: string;
  customerId: string;
  userId?: string;
  provider?: TransferProvider;
  connectionId: string;
  channelId?: string;
  playlistId?: string;
  playlistItemId?: string;
  youtubeVideoId?: string;
  youtubeVideoUrl?: string;
  title?: string;
  description?: string;
  privacyStatus?: 'private' | 'unlisted' | 'public';
  selfDeclaredMadeForKids?: boolean;
  uploadSessionRef?: string;
  projectId: string;
  projectName?: string;
  lessonId: string;
  lessonTitle?: string;
  lessonOrder?: number;
  renderOutputId: string;
  sourceStoragePath: string;
  driveFolderId?: string;
  driveFileId?: string;
  driveFileUrl?: string;
  fileName: string;
  fileSize: number;
  transferredBytes: number;
  status: TransferJobStatus;
  attemptCount: number;
  maxAttempts: number;
  workerId?: string;
  leaseExpiresAt?: string;
  lastHeartbeatAt?: string;
  errorCode?: string;
  errorMessage?: string;
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
}

export type DownloadJobStatus =
  | 'queued'
  | 'preparing'
  | 'ready'
  | 'error'
  | 'expired';

export interface DownloadJob {
  id: string;
  customerId: string;
  createdBy: string;
  projectId?: string;
  projectName?: string;
  lessonIds: string[];
  renderOutputIds: string[];
  fileCount: number;
  totalBytes: number;
  outputStoragePath?: string;
  downloadUrl?: string;
  status: DownloadJobStatus;
  progress: number;
  createdAt: string;
  completedAt?: string;
  expiresAt: string;
  errorMessage?: string;
}


